// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { ExpandMore } from '@/constants/hardfin-standard-icons';
import { useLocalStorage } from '@/hooks/use-local-storage';
import type { CycleTimeMetric } from '@/report/api/types';
import { CycleTimeGantt } from '@/report/components/cycle-time-gantt';
import type { CycleTimeSegment } from '@/report/components/cycle-time-metrics';
import { formatCycleTimeDays } from '@/report/components/cycle-time-metrics';
import { CycleTimeSummaryCards } from '@/report/components/cycle-time-summary-cards';
import { ReportDateRangeInput } from '@/report/components/report-date-range-input';
import { pluralize } from '@/utilities/pluralize';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import type { DateTime, IANAZone, Interval } from 'luxon';
import { SyntheticEvent } from 'react';
import { z } from 'zod';

const CYCLE_TIME_INVALID_DATE_RANGE_MESSAGE =
  'Start date cannot be after end date';

interface CycleTimeReportQuery<R> {
  report: R | null;
  isPlaceholderData: boolean;
}

/**
 * Domain configuration for the shared cycle-time analytics accordion:
 * segment definitions, copy nouns, DOM/storage keys, and the report query
 * hook that loads the domain's cycle-time report.
 */
interface CycleTimeAnalyticsConfigProps<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
> {
  segments: readonly CycleTimeSegment<K>[];
  singularNoun: string;
  domIdPrefix: string;
  summarySkeletonWidthPx: number;
  getTotalCount: (report: R) => number;
}

interface CycleTimeAnalyticsSectionProps<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
> extends CycleTimeAnalyticsConfigProps<K, R> {
  interval: Interval;
  timezone: IANAZone;
  isDateRangeInvalid: boolean;
  onDateRangeChange: (dateRange: DateRange<DateTime>) => void;
  expandedStorageKey: string;
  errorBoundaryMessage: string;
  useReportQuery: (filter: { interval: Interval }) => CycleTimeReportQuery<R>;
}

interface CycleTimeAnalyticsContentProps<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
> extends CycleTimeAnalyticsConfigProps<K, R> {
  interval: Interval;
  timezone: IANAZone;
  isDateRangeInvalid: boolean;
  report: R | null;
  isExpanded: boolean;
  isLoading?: boolean;
  isPlaceholderData?: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onDateRangeChange: (dateRange: DateRange<DateTime>) => void;
}

type CycleTimeAnalyticsSectionLoaderProps<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
> = Omit<
  CycleTimeAnalyticsContentProps<K, R>,
  'report' | 'isLoading' | 'isPlaceholderData'
> & {
  useReportQuery: (filter: { interval: Interval }) => CycleTimeReportQuery<R>;
};

function cycleTimeSummaryText<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
>(
  report: R,
  segments: readonly CycleTimeSegment<K>[],
  singularNoun: string,
  getTotalCount: (report: R) => number
): string | null {
  const medians = segments.map((segment) => report[segment.key].medianDays);
  const allMediansPresent = medians.every(
    (median): median is number => median !== null
  );

  if (!allMediansPresent) {
    return null;
  }

  const totalDays = medians.reduce((sum, median) => sum + median, 0);
  const equation = medians.map(formatCycleTimeDays).join(' + ');
  return `${equation} = ${formatCycleTimeDays(totalDays)} (${pluralize(getTotalCount(report), { singular: singularNoun })})`;
}

export function CycleTimeAnalyticsSection<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
>(props: CycleTimeAnalyticsSectionProps<K, R>) {
  const [isExpandedPreference, setIsExpandedPreference] = useLocalStorage(
    props.expandedStorageKey,
    z.boolean()
  );
  const contentProps = {
    interval: props.interval,
    timezone: props.timezone,
    isDateRangeInvalid: props.isDateRangeInvalid,
    segments: props.segments,
    singularNoun: props.singularNoun,
    domIdPrefix: props.domIdPrefix,
    summarySkeletonWidthPx: props.summarySkeletonWidthPx,
    getTotalCount: props.getTotalCount,
    isExpanded: isExpandedPreference ?? false,
    onDateRangeChange: props.onDateRangeChange,
    onExpandedChange: setIsExpandedPreference,
  };

  if (props.isDateRangeInvalid) {
    return (
      <CycleTimeAnalyticsContent
        {...contentProps}
        report={null}
        isLoading={false}
      />
    );
  }

  return (
    <SupportErrorBoundary message={props.errorBoundaryMessage}>
      <CycleTimeAnalyticsSectionLoader
        {...contentProps}
        useReportQuery={props.useReportQuery}
      />
    </SupportErrorBoundary>
  );
}

function CycleTimeAnalyticsContent<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
>(props: CycleTimeAnalyticsContentProps<K, R>) {
  const isLoading = props.isLoading ?? props.report === null;
  const showLoadingIndicator = isLoading || props.isPlaceholderData === true;
  const report = showLoadingIndicator ? null : props.report;
  const summaryText =
    report === null
      ? null
      : cycleTimeSummaryText(
          report,
          props.segments,
          props.singularNoun,
          props.getTotalCount
        );

  const handleAccordionChange = (_event: SyntheticEvent, expanded: boolean) => {
    props.onExpandedChange(expanded);
  };

  return (
    <Box sx={{ position: 'relative', minHeight: 56, mb: 2 }}>
      {showLoadingIndicator && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            borderRadius: '4px 4px 0 0',
          }}
        />
      )}
      <Accordion
        disableGutters
        elevation={0}
        expanded={props.isExpanded}
        onChange={handleAccordionChange}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls={`${props.domIdPrefix}-content`}
          id={`${props.domIdPrefix}-header`}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0 }}
          >
            <Typography variant="subtitle1" sx={{ flexShrink: 0 }}>
              Cycle time:
            </Typography>
            {props.isDateRangeInvalid ? (
              <Typography variant="body2" color="text.secondary">
                Invalid date range
              </Typography>
            ) : (
              <>
                {report === null ? (
                  <Skeleton
                    variant="text"
                    width={props.summarySkeletonWidthPx}
                    height={24}
                  />
                ) : summaryText !== null ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: 0,
                      whiteSpace: 'normal',
                      textWrap: 'pretty',
                    }}
                  >
                    {summaryText}
                  </Typography>
                ) : null}
              </>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="flex-end">
              <ReportDateRangeInput
                interval={props.interval}
                timezone={props.timezone}
                onChange={props.onDateRangeChange}
                hasDateRange={!props.isDateRangeInvalid}
                error={props.isDateRangeInvalid}
                helperText={
                  props.isDateRangeInvalid
                    ? CYCLE_TIME_INVALID_DATE_RANGE_MESSAGE
                    : undefined
                }
                label="Created date range"
              />
            </Stack>
            {props.isDateRangeInvalid ? (
              <Alert severity="info">
                {CYCLE_TIME_INVALID_DATE_RANGE_MESSAGE}
              </Alert>
            ) : (
              <Stack spacing={2}>
                <CycleTimeSummaryCards
                  report={report}
                  segments={props.segments}
                  singularNoun={props.singularNoun}
                />
                <CycleTimeGantt
                  report={report}
                  segments={props.segments}
                  singularNoun={props.singularNoun}
                  isLoading={showLoadingIndicator}
                />
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

function CycleTimeAnalyticsSectionLoader<
  K extends string,
  R extends Record<K, CycleTimeMetric>,
>(props: CycleTimeAnalyticsSectionLoaderProps<K, R>) {
  const { useReportQuery, ...contentProps } = props;
  const reportQuery = useReportQuery({ interval: props.interval });

  return (
    <CycleTimeAnalyticsContent
      {...contentProps}
      report={reportQuery.report}
      isPlaceholderData={reportQuery.isPlaceholderData}
    />
  );
}
